import React, { useState } from "react";
import { Box, Dialog, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { motion } from "framer-motion";

export default function ImageMessage({ msg, handleDownload }) {
  const [previewOpen, setPreviewOpen] = useState(false);

  const openPreview = () => setPreviewOpen(true);
  const closePreview = () => setPreviewOpen(false);

  const downloadImage = (event) => {
    event?.stopPropagation();
    handleDownload(msg.content, msg.originalPrompt);
  };

  const handleImageKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPreview();
    }
  };

  return (
    <Box sx={{ position: "relative", lineHeight: 0 }}>
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 3,
          display: "flex",
          gap: 1,
        }}
      >
        <Tooltip title="Expand image">
          <IconButton
            component={motion.button}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Expand generated image"
            onClick={(event) => {
              event.stopPropagation();
              openPreview();
            }}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.3)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton
            component={motion.button}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Download generated image"
            onClick={downloadImage}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.3)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        component="img"
        src={msg.content}
        alt="Generated result"
        role="button"
        tabIndex={0}
        onClick={openPreview}
        onKeyDown={handleImageKeyDown}
        sx={{
          width: 350,
          maxWidth: "100%",
          aspectRatio: "1 / 1",
          objectFit: "cover",
          display: "block",
          borderRadius: "16px",
          cursor: "zoom-in",
        }}
      />

      <Dialog
        open={previewOpen}
        onClose={closePreview}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "min(1100px, 94vw)",
            maxWidth: "94vw",
            maxHeight: "92vh",
            m: 0,
            borderRadius: "24px",
            overflow: "hidden",
            bgcolor: "rgba(10,12,22,0.82)",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
          },
        }}
        BackdropProps={{
          sx: {
            bgcolor: "rgba(0,0,0,0.72)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 1.5, sm: 2.5 },
            minHeight: "min(620px, 70vh)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 2,
              display: "flex",
              gap: 1,
            }}
          >
            <Tooltip title="Download">
              <IconButton
                aria-label="Download generated image"
                onClick={downloadImage}
                sx={{
                  bgcolor: "rgba(255,255,255,0.16)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.25)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.26)" },
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                aria-label="Close image preview"
                onClick={closePreview}
                sx={{
                  bgcolor: "rgba(255,255,255,0.16)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.25)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.26)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            component="img"
            src={msg.content}
            alt="Generated result preview"
            sx={{
              width: "100%",
              height: "100%",
              maxWidth: "100%",
              maxHeight: "86vh",
              objectFit: "contain",
              borderRadius: "18px",
              boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
            }}
          />
        </Box>
      </Dialog>
    </Box>
  );
}